import axios from "axios";

// The stable codes contractsapi sends on a rejected terms save. Only the ones
// this screen words for itself are listed; every other code falls back to the
// generic message, so a new server-side rule cannot break the build.
export enum TermsErrorCode {
  // The correction could not withdraw the scheduled amendment, because that
  // amendment stopped being pending mid-save — applied, withdrawn by someone
  // else, or lost to a write conflict. Nothing was written, and the terms on
  // screen are stale. Retryable once reloaded.
  PENDING_AMENDMENT_WITHDRAWN = "PENDING_AMENDMENT_WITHDRAWN",
  // A second future-dated version was refused. Reachable from the amendment
  // path only: a correction withdraws the pending version instead.
  PENDING_VERSION_EXISTS = "PENDING_VERSION_EXISTS"
}

const MESSAGE_KEY_BY_CODE: Record<TermsErrorCode, string> = {
  [TermsErrorCode.PENDING_AMENDMENT_WITHDRAWN]:
    "contracts.updateTerms.editor.saveErrorAmendmentChanged",
  [TermsErrorCode.PENDING_VERSION_EXISTS]:
    "contracts.updateTerms.editor.saveErrorPendingVersionExists"
};

const GENERIC_KEY = "contracts.updateTerms.editor.saveError";

// `in` walks the prototype chain, so a body whose `code` is "toString" or
// "constructor" would pass and the lookup would return an inherited function
// rather than a key. Own-property only.
const isKnownCode = (code: unknown): code is TermsErrorCode =>
  typeof code === "string" &&
  Object.prototype.hasOwnProperty.call(MESSAGE_KEY_BY_CODE, code);

// The i18n key for a failed terms save. Reads the structured body contractsapi
// returns on a 400 (`{ code, message, itemIndex, field }`) and words the
// pending-version rejections for the user; anything else — a 500, a network
// failure, an unrecognised code — keeps the generic message.
//
// The server's own `message` is deliberately not shown: it is written for an
// engineer reading a log, and it is not translated.
export const saveErrorMessageKey = (error: unknown): string => {
  const code = termsErrorCode(error);
  return isKnownCode(code) ? MESSAGE_KEY_BY_CODE[code] : GENERIC_KEY;
};

// Whether the save was refused because the scheduled amendment stopped being
// pending before the correction could withdraw it. The warning shown before
// saving is out of date once this happens, so the caller re-reads the versions.
export const isAmendmentNoLongerPending = (error: unknown): boolean =>
  termsErrorCode(error) === TermsErrorCode.PENDING_AMENDMENT_WITHDRAWN;

// The 400 body contractsapi returns for a rejected terms save. Only `code` is
// read here — see above for why the server's `message` is not surfaced.
interface TermsErrorBody {
  code?: unknown;
}

export const termsErrorCode = (error: unknown): unknown => {
  if (!axios.isAxiosError(error)) return undefined;
  return (error.response?.data as TermsErrorBody | undefined)?.code;
};
